import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Pencil,
  Lock,
  Power,
  Trash2,
  ChevronDown,
  Loader2,
  MessageCircle,
  Download,
  Copy,
  MoreHorizontal,
  KeyRound,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import type { ChatbotUser, Tenant, ConversationLog, ChatMessageLog } from '@/core/models';

export const Route = createFileRoute('/users')({
  head: () => ({
    meta: [{ title: 'Utenze Chatbot · Replya Internal Backoffice' }],
  }),
  component: UsersPage,
});

function getRandomChar(chars: string): string {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return chars[array[0] % chars.length];
}

function generatePassword(length = 12): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '@$!%*?&_#^~+=-';
  const all = uppercase + lowercase + digits + special;

  const password = [
    getRandomChar(uppercase),
    getRandomChar(lowercase),
    getRandomChar(digits),
    getRandomChar(special),
  ];

  for (let i = password.length; i < length; i++) {
    password.push(getRandomChar(all));
  }

  // Shuffle Fisher-Yates tramite crypto.getRandomValues.
  for (let i = password.length - 1; i > 0; i--) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const j = array[0] % (i + 1);
    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join('');
}

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  user?: ChatbotUser | null;
  tenants: Tenant[];
  defaultTenantId?: string;
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
    tenantIds: string[];
    disabled?: boolean;
    mustChangePassword?: boolean;
    sendWelcomeEmail?: boolean;
  }) => void;
  isPending: boolean;
}

function TenantMultiSelect({
  tenants,
  selected,
  onChange,
}: {
  tenants: Tenant[];
  selected: string[];
  onChange: (value: string[]) => void;
}) {
  const toggle = (tenantId: string) => {
    onChange(
      selected.includes(tenantId)
        ? selected.filter((id) => id !== tenantId)
        : [...selected, tenantId],
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between rounded-xl bg-surface-2 border-border"
        >
          <span className="truncate">
            {selected.length === 0
              ? 'Seleziona tenant'
              : `${selected.length} tenant selezionati`}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        {tenants.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            Nessun tenant disponibile
          </div>
        ) : (
          tenants.map((t) => (
            <DropdownMenuCheckboxItem
              key={t.tenantId}
              checked={selected.includes(t.tenantId)}
              onCheckedChange={() => toggle(t.tenantId)}
            >
              {t.name}
            </DropdownMenuCheckboxItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserFormDialog({
  open,
  onOpenChange,
  mode,
  user,
  tenants,
  defaultTenantId,
  onSubmit,
  isPending,
}: UserFormDialogProps) {
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [tenantIds, setTenantIds] = useState<string[]>(
    user?.tenantIds ?? (defaultTenantId ? [defaultTenantId] : []),
  );
  const [disabled, setDisabled] = useState(user?.disabled ?? false);
  const [mustChangePassword, setMustChangePassword] = useState(true);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);

  const canSubmit = firstName.trim() && lastName.trim() && email.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      tenantIds,
      disabled: mode === 'edit' ? disabled : undefined,
      mustChangePassword: mode === 'create' ? mustChangePassword : undefined,
      sendWelcomeEmail: mode === 'create' ? sendWelcomeEmail : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Nuova utenza chatbot' : 'Modifica utenza chatbot'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Crea un nuovo utente Firebase con password generata automaticamente e associa i tenant.'
              : 'Aggiorna nome, email, tenant e stato dell\'utenza.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="user-firstname">Nome</Label>
              <Input
                id="user-firstname"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Mario"
                className="rounded-xl bg-surface-2 border-border"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-lastname">Cognome</Label>
              <Input
                id="user-lastname"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Rossi"
                className="rounded-xl bg-surface-2 border-border"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-email">Email</Label>
            <Input
              id="user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mario.rossi@example.com"
              className="rounded-xl bg-surface-2 border-border"
            />
          </div>
          <div className="space-y-2">
            <Label>Tenant associati</Label>
            <TenantMultiSelect
              tenants={tenants}
              selected={tenantIds}
              onChange={setTenantIds}
            />
          </div>
          {mode === 'edit' && (
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="user-disabled" className="text-sm">
                  Account bloccato
                </Label>
                <p className="text-xs text-muted-foreground">
                  Disabilita l'accesso al chatbot per questo utente.
                </p>
              </div>
              <Switch
                id="user-disabled"
                checked={disabled}
                onCheckedChange={setDisabled}
              />
            </div>
          )}
          {mode === 'create' && (
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="user-must-change-password" className="text-sm">
                  Obbliga cambio password al primo accesso
                </Label>
                <p className="text-xs text-muted-foreground">
                  Se attivo, l'utente dovrà impostare una nuova password al primo login.
                </p>
              </div>
              <Switch
                id="user-must-change-password"
                checked={mustChangePassword}
                onCheckedChange={setMustChangePassword}
              />
            </div>
          )}
          {mode === 'create' && (
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="user-send-welcome-email" className="text-sm">
                  Invia email di benvenuto
                </Label>
                <p className="text-xs text-muted-foreground">
                  Se attivo, al backend verrà richiesto l'invio automatico delle credenziali all'utente.
                </p>
              </div>
              <Switch
                id="user-send-welcome-email"
                checked={sendWelcomeEmail}
                onCheckedChange={setSendWelcomeEmail}
              />
            </div>
          )}
          <DialogFooter>
            <Button
              type="submit"
              disabled={isPending || !canSubmit}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              {isPending
                ? 'Salvataggio...'
                : mode === 'create'
                  ? 'Crea utenza'
                  : 'Salva modifiche'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserConversationsDialog({
  open,
  onOpenChange,
  user,
  tenantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ChatbotUser;
  tenantId: string;
}) {
  const [selectedConversation, setSelectedConversation] = useState<ConversationLog | null>(null);

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['user-conversations', tenantId, user.firebaseId],
    queryFn: () =>
      apiService.getConversationLogs(tenantId, { firebaseId: user.firebaseId, limit: 100 }),
    enabled: open,
  });

  const { data: messages, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['conversation-messages', tenantId, selectedConversation?.conversationId],
    queryFn: () =>
      apiService.getChatMessageLogs(tenantId, {
        conversationId: selectedConversation!.conversationId,
        limit: 500,
      }),
    enabled: !!selectedConversation,
  });

  const handleExportConversation = (conversation: ConversationLog, msgs: ChatMessageLog[]) => {
    const lines = [
      `Conversazione: ${conversation.conversationId}`,
      `Utente: ${user.firstName} ${user.lastName} <${user.email}>`,
      `Tenant: ${tenantId}`,
      `Messaggi: ${conversation.messageCount ?? msgs.length}`,
      `Dal: ${conversation.firstMessageAt ? new Date(conversation.firstMessageAt).toLocaleString('it-IT') : '-'}`,
      `Al: ${conversation.lastMessageAt ? new Date(conversation.lastMessageAt).toLocaleString('it-IT') : '-'}`,
      '---',
      ...msgs.map((m) => {
        const when = m.createdAt ? new Date(m.createdAt).toLocaleString('it-IT') : '';
        const who = m.role === 'assistant' ? 'Assistente' : m.role === 'system' ? 'Sistema' : 'Utente';
        return `[${when}] ${who}:\n${m.content}`;
      }),
    ];
    const blob = new Blob([lines.join('\n\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversazione-${conversation.conversationId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    if (!conversations?.items.length) return;
    const summary = conversations.items.map((c) => ({
      conversationId: c.conversationId,
      messageCount: c.messageCount ?? 0,
      firstMessageAt: c.firstMessageAt,
      lastMessageAt: c.lastMessageAt,
    }));
    const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversazioni-${user.firebaseId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {selectedConversation
              ? `Conversazione ${selectedConversation.conversationId.slice(0, 12)}...`
              : `Conversazioni di ${user.firstName} ${user.lastName}`}
          </DialogTitle>
          <DialogDescription>
            {selectedConversation
              ? 'Leggi i messaggi scambiati e scarica l\'audit.'
              : 'Elenco delle conversazioni effettuate con il chatbot.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {selectedConversation ? (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => setSelectedConversation(null)}
                >
                  ← Indietro
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() =>
                    messages && handleExportConversation(selectedConversation, messages.items)
                  }
                  disabled={!messages || isLoadingMessages}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Esporta
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Caricamento messaggi...
                  </div>
                ) : messages?.items.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Nessun messaggio trovato.</p>
                ) : (
                  messages?.items.map((msg) => (
                    <div
                      key={msg.id}
                      className={`rounded-2xl p-3 text-sm ${
                        msg.role === 'assistant'
                          ? 'bg-surface-2'
                          : msg.role === 'system'
                            ? 'bg-muted'
                            : 'bg-brand text-brand-foreground ml-auto'
                      } ${msg.role === 'user' ? 'max-w-[80%]' : 'max-w-[80%]'}`}
                    >
                      <div className="text-xs opacity-70 mb-1">
                        {msg.role === 'assistant'
                          ? 'Assistente'
                          : msg.role === 'system'
                            ? 'Sistema'
                            : 'Utente'}
                        {' · '}
                        {msg.createdAt ? new Date(msg.createdAt).toLocaleString('it-IT') : ''}
                      </div>
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">
                  {conversations?.total ?? 0} conversazioni
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={handleExportAll}
                  disabled={!conversations?.items.length}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Esporta elenco
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto pr-2">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Caricamento conversazioni...
                  </div>
                ) : conversations?.items.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nessuna conversazione trovata per questo utente.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {conversations?.items.map((conversation) => (
                      <div
                        key={conversation.conversationId}
                        className="flex items-center justify-between rounded-xl border bg-surface p-3 hover:bg-surface-2 transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {conversation.summary || `Conversazione ${conversation.conversationId}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {conversation.messageCount ?? 0} messaggi ·{' '}
                            {conversation.lastMessageAt
                              ? new Date(conversation.lastMessageAt).toLocaleString('it-IT')
                              : '-'}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => setSelectedConversation(conversation)}
                          >
                            Apri
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="rounded-xl"
                            title="Esporta conversazione"
                            onClick={async () => {
                              const msgs = await apiService.getChatMessageLogs(tenantId, {
                                conversationId: conversation.conversationId,
                                limit: 500,
                              });
                              handleExportConversation(conversation, msgs.items);
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsersPage() {
  const queryClient = useQueryClient();
  const { selectedTenant } = useAppState();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ChatbotUser | null>(null);
  const [resetUser, setResetUser] = useState<ChatbotUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<ChatbotUser | null>(null);
  const [regenerateUser, setRegenerateUser] = useState<ChatbotUser | null>(null);
  const [conversationUser, setConversationUser] = useState<ChatbotUser | null>(null);
  const [filterByTenant, setFilterByTenant] = useState(true);
  const [createdCredentials, setCreatedCredentials] = useState<{
    email: string;
    password: string;
    regenerated?: boolean;
    emailSent?: boolean;
  } | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiService.listTenants(),
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ['chatbot-users'],
    queryFn: () => apiService.listUsers(),
  });

  const createMutation = useMutation({
    mutationFn: (body: Parameters<typeof apiService.createUser>[0]) =>
      apiService.createUser(body),
    onSuccess: () => {
      toast.success('Utenza chatbot creata');
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ['chatbot-users'] });
    },
    onError: (error: Error) => {
      setCreatedCredentials(null);
      toast.error(error.message || 'Errore durante la creazione');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      firebaseId,
      body,
    }: {
      firebaseId: string;
      body: Parameters<typeof apiService.updateUser>[1];
    }) => apiService.updateUser(firebaseId, body),
    onSuccess: () => {
      toast.success('Utenza chatbot aggiornata');
      setEditingUser(null);
      queryClient.invalidateQueries({ queryKey: ['chatbot-users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante l\'aggiornamento');
    },
  });

  const disableMutation = useMutation({
    mutationFn: (firebaseId: string) => apiService.disableUser(firebaseId),
    onSuccess: () => {
      toast.success('Utenza bloccata');
      queryClient.invalidateQueries({ queryKey: ['chatbot-users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante il blocco');
    },
  });

  const enableMutation = useMutation({
    mutationFn: (firebaseId: string) => apiService.enableUser(firebaseId),
    onSuccess: () => {
      toast.success('Utenza sbloccata');
      queryClient.invalidateQueries({ queryKey: ['chatbot-users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante lo sblocco');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => apiService.resetUserPassword(email),
    onSuccess: (result) => {
      if (result.emailSent) {
        toast.success(result.message || 'Email di reset inviata');
      } else {
        toast.warning(
          result.message || 'Link generato, ma l\'email non è stata inviata',
        );
      }
      if (result.link) {
        toast.info(`Link reset: ${result.link}`, {
          duration: 10000,
          description:
            'Il link è valido 1 ora ed è monouso: se il client di posta o un antivirus lo apre in anteprima risulterà scaduto.',
        });
      }
      setResetUser(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante il reset');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (firebaseId: string) => apiService.regenerateUserPassword(firebaseId),
    onSuccess: (result) => {
      setCreatedCredentials({
        email: result.email,
        password: result.password,
        regenerated: true,
        emailSent: result.emailSent,
      });
      if (result.emailSent) {
        toast.success(result.message || 'Nuove credenziali generate e inviate');
      } else {
        toast.warning(result.message || 'Nuove credenziali generate (email non inviata)');
      }
      setRegenerateUser(null);
      queryClient.invalidateQueries({ queryKey: ['chatbot-users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante la rigenerazione delle credenziali');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (firebaseId: string) => apiService.deleteUser(firebaseId),
    onSuccess: () => {
      toast.success('Utenza eliminata');
      setDeleteUser(null);
      queryClient.invalidateQueries({ queryKey: ['chatbot-users'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Errore durante l\'eliminazione');
    },
  });

  const tenantName = (tenantId: string) =>
    tenants?.find((t) => t.tenantId === tenantId)?.name ?? tenantId;

  const filteredUsers = useMemo(() => {
    if (!filterByTenant || !selectedTenant) return users ?? [];
    return (users ?? []).filter((u) => u.tenantIds.includes(selectedTenant.tenantId));
  }, [users, selectedTenant, filterByTenant]);

  const handleCreate = (data: {
    firstName: string;
    lastName: string;
    email: string;
    tenantIds: string[];
    mustChangePassword?: boolean;
    sendWelcomeEmail?: boolean;
  }) => {
    const generatedPassword = generatePassword();
    setCreatedCredentials({ email: data.email, password: generatedPassword });
    createMutation.mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: generatedPassword,
      tenantIds: data.tenantIds,
      sendWelcomeEmail: data.sendWelcomeEmail ?? false,
      mustChangePassword: data.mustChangePassword,
    });
  };

  const handleEdit = (data: {
    firstName: string;
    lastName: string;
    email: string;
    tenantIds: string[];
    disabled?: boolean;
  }) => {
    if (!editingUser) return;
    const body: Parameters<typeof apiService.updateUser>[1] = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      tenantIds: data.tenantIds,
    };
    updateMutation.mutate(
      { firebaseId: editingUser.firebaseId, body },
      {
        onSuccess: () => {
          if (data.disabled !== undefined && data.disabled !== editingUser.disabled) {
            if (data.disabled) {
              disableMutation.mutate(editingUser.firebaseId);
            } else {
              enableMutation.mutate(editingUser.firebaseId);
            }
          }
        },
      },
    );
  };

  const handleToggle = (user: ChatbotUser) => {
    if (user.disabled) {
      enableMutation.mutate(user.firebaseId);
    } else {
      disableMutation.mutate(user.firebaseId);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Utenze Chatbot"
        subtitle="Crea, leggi, blocca e modifica le utenze generate per il chatbot."
      >
        <Button
          onClick={() => setCreateOpen(true)}
          className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuova utenza
        </Button>
      </PageHeader>

      <UserFormDialog
        key={`create-${createOpen}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        tenants={tenants ?? []}
        defaultTenantId={selectedTenant?.tenantId}
        onSubmit={handleCreate}
        isPending={createMutation.isPending}
      />

      <UserFormDialog
        key={editingUser?.firebaseId ?? 'edit-closed'}
        open={editingUser !== null}
        onOpenChange={(open) => !open && setEditingUser(null)}
        mode="edit"
        user={editingUser}
        tenants={tenants ?? []}
        onSubmit={handleEdit}
        isPending={updateMutation.isPending}
      />

      {conversationUser && selectedTenant && (
        <UserConversationsDialog
          open={conversationUser !== null}
          onOpenChange={(open) => !open && setConversationUser(null)}
          user={conversationUser}
          tenantId={selectedTenant.tenantId}
        />
      )}

      <AlertDialog
        open={resetUser !== null}
        onOpenChange={(open) => !open && setResetUser(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi inviare l'email di reset password a{' '}
              <span className="font-medium text-foreground">{resetUser?.email}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resetUser && resetPasswordMutation.mutate(resetUser.email)}
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              {resetPasswordMutation.isPending ? 'Invio...' : 'Invia email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteUser !== null}
        onOpenChange={(open) => !open && setDeleteUser(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina utenza</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare{' '}
              <span className="font-medium text-foreground">
                {deleteUser?.firstName} {deleteUser?.lastName}
              </span>{' '}
              (<code>{deleteUser?.email}</code>)?
              <br />
              L'azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteUser && deleteMutation.mutate(deleteUser.firebaseId)}
              className="rounded-xl bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={regenerateUser !== null}
        onOpenChange={(open) => !open && setRegenerateUser(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Rigenera credenziali</AlertDialogTitle>
            <AlertDialogDescription>
              Viene generata una nuova password provvisoria per{' '}
              <span className="font-medium text-foreground">
                {regenerateUser?.firstName} {regenerateUser?.lastName}
              </span>{' '}
              (<code>{regenerateUser?.email}</code>).
              <br />
              La password attuale smette di funzionare subito. A differenza del link di
              reset, la nuova password non scade e la vedrai qui per copiarla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                regenerateUser && regenerateMutation.mutate(regenerateUser.firebaseId)
              }
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
            >
              {regenerateMutation.isPending ? 'Generazione...' : 'Rigenera'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={createdCredentials !== null} onOpenChange={() => setCreatedCredentials(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Credenziali generate</DialogTitle>
            <DialogDescription>
              {createdCredentials?.regenerated
                ? "La password è stata rigenerata: quella precedente non è più valida. Condividi le nuove credenziali con l'utente. Al primo accesso dovrà cambiarla."
                : "L'utenza è stata creata. Condividi le credenziali con l'utente oppure invia un'email di reset password. Al primo accesso dovrà cambiare la password."}
            </DialogDescription>
          </DialogHeader>
          {createdCredentials && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="cred-email">Email</Label>
                <div className="flex gap-2">
                  <Input
                    id="cred-email"
                    value={createdCredentials.email}
                    readOnly
                    className="rounded-xl bg-surface-2 border-border"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="rounded-xl shrink-0"
                    onClick={() => navigator.clipboard.writeText(createdCredentials.email)}
                    title="Copia email"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cred-password">Password generata</Label>
                <div className="flex gap-2">
                  <Input
                    id="cred-password"
                    type="text"
                    value={createdCredentials.password}
                    readOnly
                    className="rounded-xl bg-surface-2 border-border font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="rounded-xl shrink-0"
                    onClick={() => navigator.clipboard.writeText(createdCredentials.password)}
                    title="Copia password"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {createdCredentials.regenerated ? (
                <p className="text-xs text-muted-foreground">
                  {createdCredentials.emailSent
                    ? 'Le nuove credenziali sono state inviate anche via email all\'utente.'
                    : 'L\'email con le nuove credenziali non è stata inviata: copiala e condividila tu.'}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  L'email di benvenuto viene inviata solo se hai attivato l'apposito switch in fase di creazione.
                  Se non è stata inviata automaticamente, puoi farlo ora con il pulsante qui sotto.
                </p>
              )}
              <p className="text-xs text-amber-600">
                Attenzione: il link di reset di Firebase è valido 1 ora ed è monouso. Se l'utente non lo
                usa subito o se il client di posta/antivirus lo apre in anteprima, risulterà scaduto.
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={() => setCreatedCredentials(null)}
            >
              Chiudi
            </Button>
            <Button
              type="button"
              className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
              disabled={resetPasswordMutation.isPending || !createdCredentials}
              onClick={() => createdCredentials && resetPasswordMutation.mutate(createdCredentials.email)}
            >
              {resetPasswordMutation.isPending ? 'Invio...' : 'Invia email di reset password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedTenant && (
        <div className="flex items-center justify-between rounded-2xl border bg-surface p-3">
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="text-xs">{selectedTenant.name}</Badge>
            <span className="text-sm text-muted-foreground">
              {filteredUsers.length} utenze
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="filter-by-tenant" className="text-sm">
              Solo tenant selezionato
            </Label>
            <Switch
              id="filter-by-tenant"
              checked={filterByTenant}
              onCheckedChange={setFilterByTenant}
            />
          </div>
        </div>
      )}

      <div className="rounded-2xl border bg-surface overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Password</TableHead>
                <TableHead>Ultimo accesso</TableHead>
                <TableHead>Email verificata</TableHead>
                <TableHead>Creato il</TableHead>
                <TableHead className="w-16 sticky right-0 bg-surface"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    {filterByTenant && selectedTenant
                      ? `Nessuna utenza chatbot trovata per ${selectedTenant.name}.`
                      : 'Nessuna utenza chatbot trovata.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.firebaseId}>
                    <TableCell className="font-medium">
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.tenantIds.length === 0 ? (
                          <span className="text-xs text-muted-foreground">Nessun tenant</span>
                        ) : (
                          user.tenantIds.map((tid) => (
                            <Badge key={tid} variant="secondary" className="text-xs">
                              {tenantName(tid)}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.disabled ? (
                        <Badge variant="destructive" className="text-xs">
                          Bloccato
                        </Badge>
                      ) : (
                        <Badge
                          variant="default"
                          className="text-xs bg-emerald-600 hover:bg-emerald-700"
                        >
                          Attivo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.mustChangePassword ? (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-600">
                          Da cambiare
                        </Badge>
                      ) : user.passwordChangedAt ? (
                        <span className="text-xs text-muted-foreground">
                          {new Date(user.passwordChangedAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.lastSignInAt ? (
                        <span className="text-xs text-muted-foreground">
                          {new Date(user.lastSignInAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Mai</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.emailVerified ? (
                        <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-600">Sì</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="sticky right-0 bg-surface">
                      <div className="flex items-center justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="rounded-xl"
                              title="Azioni"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem
                              disabled={!selectedTenant}
                              onClick={() => setConversationUser(user)}
                            >
                              <MessageCircle className="mr-2 h-4 w-4" />
                              {selectedTenant
                                ? 'Conversazioni'
                                : 'Conversazioni (scegli un tenant)'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditingUser(user)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setRegenerateUser(user)}>
                              <KeyRound className="mr-2 h-4 w-4" />
                              Rigenera credenziali
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setResetUser(user)}>
                              <Lock className="mr-2 h-4 w-4" />
                              Invia link di reset
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleToggle(user)}>
                              <Power
                                className={`mr-2 h-4 w-4 ${
                                  user.disabled ? 'text-emerald-600' : 'text-destructive'
                                }`}
                              />
                              {user.disabled ? 'Sblocca' : 'Blocca'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setDeleteUser(user)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Elimina utenza
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

    </div>
  );

}
